/**
 * Operational timeline engine — the single source of truth for how a task's
 * progress feed is built, ordered, described and read.
 *
 * Everything here is pure: no Dexie, no React, no clock beyond the one the
 * caller passes in. The persistence sits behind it in
 * `src/db/timeline-store.ts` and the feed itself is rendered by
 * `src/modules/tasks/timeline/TaskTimeline.tsx` — both call into this module
 * rather than re-deriving an order or a label of their own.
 *
 * Two rules the rest of the app leans on:
 *
 *  1. **Ordering is never the caller's business.** Every read passes through
 *     {@link sortTimelineEvents}, so a feed rendered chronologically and the
 *     same feed rendered newest-first are the same list read two ways — a row
 *     can never appear in one and not the other.
 *  2. **Progress is derived, never stored.** {@link timelineProgress} reads the
 *     current step off the events themselves; a step written twice can disagree
 *     with itself, so it is written once, as an event, and nowhere else.
 */
import { ROLE_LABELS } from './permissions';
import type { TaskViewResolution } from './task-views';
import {
  TIMELINE_EVENT_LABELS,
  TIMELINE_STEP_RANK,
  TIMELINE_TERMINAL_EVENTS,
  TIMELINE_EVENT_TYPES,
} from '../types/timeline';
import type {
  TimelineEvent,
  TimelineEventDraft,
  TimelineEventType,
  TimelineOrder,
} from '../types/timeline';
import type { ISODateString } from '../types/index';
import type { User } from '../types/user';

const trimmed = (value: string | null | undefined): string => String(value ?? '').trim();

const optional = (value: string | null | undefined): string | undefined => trimmed(value) || undefined;

const pad = (value: number): string => String(value).padStart(2, '0');

/** True for a step the system actually knows — anything else is refused. */
export function isTimelineEventType(value: unknown): value is TimelineEventType {
  return TIMELINE_EVENT_TYPES.includes(value as TimelineEventType);
}

// ── Building an event ────────────────────────────────────────────────

const newTimelineId = (): string => `tml-${crypto.randomUUID()}`;

/**
 * Normalises a draft into the row that is filed: the step is validated, the
 * headline falls back to the step's own Hebrew label, and the id and the stamp
 * are ours so two callers can never mint the same event.
 */
export function buildTimelineEvent(draft: TimelineEventDraft): TimelineEvent {
  if (!trimmed(draft.taskId)) throw new Error('Timeline event needs a task');
  if (!isTimelineEventType(draft.eventType)) {
    throw new Error(`Unknown timeline event type: ${String(draft.eventType)}`);
  }

  return {
    id: draft.id ?? newTimelineId(),
    taskId: draft.taskId,
    teamId: optional(draft.teamId),
    eventType: draft.eventType,
    statusTitle: trimmed(draft.statusTitle) || TIMELINE_EVENT_LABELS[draft.eventType],
    description: optional(draft.description),
    actorId: trimmed(draft.actorId),
    actorName: trimmed(draft.actorName),
    actorRole: draft.actorRole,
    timestamp: draft.timestamp ?? new Date().toISOString(),
    locationText: optional(draft.locationText),
    metadata: draft.metadata,
  };
}

// ── Ordering ─────────────────────────────────────────────────────────

/**
 * Time order, with the lifecycle itself as the tie-breaker: two steps reported
 * inside the same millisecond still read in the order they can only have
 * happened in, and the id settles a dead heat so the list is stable.
 */
export function sortTimelineEvents(
  events: readonly TimelineEvent[],
  order: TimelineOrder = 'asc',
): TimelineEvent[] {
  const chronological = [...events].sort(
    (a, b) =>
      a.timestamp.localeCompare(b.timestamp) ||
      TIMELINE_STEP_RANK[a.eventType] - TIMELINE_STEP_RANK[b.eventType] ||
      a.id.localeCompare(b.id),
  );
  return order === 'desc' ? chronological.reverse() : chronological;
}

/** The feed of one task, in the order asked for. */
export function taskTimelineOf(
  events: readonly TimelineEvent[],
  taskId: string,
  order: TimelineOrder = 'asc',
): TimelineEvent[] {
  return sortTimelineEvents(
    events.filter((event) => event.taskId === taskId),
    order,
  );
}

/**
 * The slice of a task's feed one vehicle team may see: its own steps plus every
 * step that belongs to the task as a whole. A crew never reads another
 * vehicle's progress, exactly as it never reads another vehicle's crew.
 */
export function teamTimelineOf(
  events: readonly TimelineEvent[],
  teamId: string | null | undefined,
  order: TimelineOrder = 'asc',
): TimelineEvent[] {
  return sortTimelineEvents(
    events.filter((event) => !event.teamId || event.teamId === teamId),
    order,
  );
}

/** The most recent step reported, or `null` before anything was. */
export function latestTimelineEvent(
  events: readonly TimelineEvent[],
): TimelineEvent | null {
  return sortTimelineEvents(events, 'desc')[0] ?? null;
}

// ── Progress ─────────────────────────────────────────────────────────

export interface TimelineProgress {
  /** The step the task currently stands at, or `null` before any was reported. */
  step: TimelineEventType | null;
  /** Position of that step on the lifecycle, 0-based. */
  rank: number;
  /** 0–100, for the progress bar above the feed. */
  percent: number;
  /** True once the task was closed or cancelled — the feed is finished. */
  isClosed: boolean;
  /** How many steps were reported in total. */
  count: number;
}

const MAX_RANK = Math.max(...Object.values(TIMELINE_STEP_RANK));

/** Where the task stands, read off its events alone. */
export function timelineProgress(events: readonly TimelineEvent[]): TimelineProgress {
  const latest = latestTimelineEvent(events);
  if (!latest) return { step: null, rank: -1, percent: 0, isClosed: false, count: 0 };

  // The furthest step reached, not merely the last one filed: a note added
  // after "הגיע ליעד" must never walk the bar backwards.
  const rank = events.reduce(
    (highest, event) => Math.max(highest, TIMELINE_STEP_RANK[event.eventType]),
    0,
  );
  const isClosed = events.some((event) => TIMELINE_TERMINAL_EVENTS.includes(event.eventType));
  return {
    step: latest.eventType,
    rank,
    percent: isClosed ? 100 : Math.round((rank / MAX_RANK) * 100),
    isClosed,
    count: events.length,
  };
}

// ── Reading an event ─────────────────────────────────────────────────

/**
 * The actor badge the feed renders: "ישראל (חייל)", "בן פרץ (מנהל על)". The
 * role label comes from the permissions engine, so a rename lands everywhere
 * at once.
 */
export function timelineActorBadge(event: TimelineEvent): string {
  const name = trimmed(event.actorName) || 'משתמש לא ידוע';
  return `${name} (${ROLE_LABELS[event.actorRole] ?? event.actorRole})`;
}

/** Midnight-to-midnight distance in days — the only comparison the feed needs. */
function calendarDayDiff(when: Date, reference: Date): number {
  const a = new Date(when.getFullYear(), when.getMonth(), when.getDate()).getTime();
  const b = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

/**
 * A stamp as a phone in the field reads it: "היום, 14:25", "אתמול, 09:03", and
 * `12/05, 14:25` once it is older than that. Never a bare ISO string — a driver
 * should not have to parse a timestamp to know when something happened.
 */
export function formatFieldTime(
  timestamp: ISODateString,
  now: number = Date.now(),
): string {
  if (!timestamp) return '';
  const when = new Date(timestamp);
  if (Number.isNaN(when.getTime())) return '';

  const time = `${pad(when.getHours())}:${pad(when.getMinutes())}`;
  const diff = calendarDayDiff(when, new Date(now));
  if (diff === 0) return `היום, ${time}`;
  if (diff === -1) return `אתמול, ${time}`;
  if (diff === 1) return `מחר, ${time}`;
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)}, ${time}`;
}

/** One line per event, for a plain-text feed or a report. */
export function describeTimelineEvent(
  event: TimelineEvent,
  now: number = Date.now(),
): string {
  const where = event.locationText ? ` · ${event.locationText}` : '';
  const what = event.description ? ` — ${event.description}` : '';
  return `${formatFieldTime(event.timestamp, now)} · ${event.statusTitle}${what} · ${timelineActorBadge(event)}${where}`;
}

// ── Who may write on the feed ────────────────────────────────────────

/**
 * May this account attach a custom note to the task's timeline?
 *
 * The answer is the view the router already resolved, and nothing else: a
 * manager writes on any task they can see, a soldier writes only on a task
 * their own vehicle rides for — and only while they may report from the field.
 * A refused view writes nothing at all. The store asks this question before it
 * files a note, so the screens and the database enforce one boundary.
 */
export function canAddTimelineNote(
  user: User | null | undefined,
  view: Pick<TaskViewResolution, 'mode' | 'canReportFromField'>,
): boolean {
  if (!user || !view) return false;
  if (view.mode === 'manager') return true;
  if (view.mode === 'soldier') return view.canReportFromField;
  return false;
}
